import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackChangedViewType } from '@air/analytics';
import { ViewTypeName } from '@air/api/types';

import { VIEW_TYPE_MENU_TOGGLE } from '~/constants/testIDs';
import { useApplyFilters } from '~/hooks/filters/useApplyFilters';
import { setCurrentViewTypeAction } from '~/store/configViews/actions';
import { availableViewTypeNamesSelector, currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { ViewTypeToggleMenuComponent } from './ViewTypeToggleMenuComponent';

export type ViewTypeToggleMenuProps = {
  canViewCustomFields?: boolean;
};

export const ViewTypeToggleMenu = memo(({ canViewCustomFields = true }: ViewTypeToggleMenuProps) => {
  const dispatch = useDispatch();
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const viewTypes = useSelector(availableViewTypeNamesSelector);
  const permissionedViewTypeNames = useMemo(
    () => viewTypes.filter((name) => (!canViewCustomFields ? name !== ViewTypeName.kanban : true)),
    [canViewCustomFields, viewTypes],
  );
  const { clearFilters } = useApplyFilters();
  const { trackChangedViewType } = useTrackChangedViewType();

  const changeCurrentView = useCallback(
    (viewTypeName: ViewTypeName) => {
      if (viewTypeName === ViewTypeName.kanban) {
        clearFilters();
      }
      dispatch(setCurrentViewTypeAction({ viewTypeName }));

      trackChangedViewType({ viewType: viewTypeName });
    },
    [dispatch, clearFilters, trackChangedViewType],
  );

  if (!currentViewTypeName) {
    return null;
  }

  return (
    <ViewTypeToggleMenuComponent
      availableViewTypeNames={permissionedViewTypeNames}
      dataTestId={VIEW_TYPE_MENU_TOGGLE}
      currentViewTypeName={currentViewTypeName}
      onChange={changeCurrentView}
    />
  );
});

ViewTypeToggleMenu.displayName = 'ViewTypeToggleMenu';
